import get from 'lodash.get'
import { getContentOne } from '@/src/utils/contentful'

export const state = {
  global: {},
  brand: {},
  contentfulLoadingStatus: 'initial', // => "loading" => "loaded" || "error"
}

export const getters = {
  billBodyText: (state) => {
    return get(state, 'global.billBodyText')
  },
  billButtonText: (state) => {
    return get(state, 'global.billButtonText')
  },
  billTitle: (state) => {
    return get(state, 'global.billTitle')
  },
  bodyFont: (state) => {
    return get(state, 'brand.theme.fields.bodyFont')
  },
  buttonFont: (state) => {
    return get(state, 'brand.theme.fields.buttonFont')
  },
  brandEnabled: (state) => {
    const status = get(state, 'brand.enabled')
    return status === 'Enabled'
  },
  brandId: (state) => {
    return get(state, 'brand.brandId')
  },
  brandName: (state) => {
    return get(state, 'brand.name')
  },
  colorBackground: (state) => {
    return get(state, 'brand.theme.fields.colorBackground')
  },
  colorFontBody: (state) => {
    return get(state, 'brand.theme.fields.colorBodyFont')
  },
  colorFontButton: (state) => {
    return get(state, 'brand.theme.fields.colorButtonFont')
  },
  colorFontHeading: (state) => {
    return get(state, 'brand.theme.fields.colorHeadingFont')
  },
  colorPrimary: (state) => {
    return get(state, 'brand.theme.fields.colorPrimary')
  },
  colorSecondary: (state) => {
    return get(state, 'brand.theme.fields.colorSecondary')
  },
  contentfulLoadingStatus: (state) => {
    return state.contentfulLoadingStatus
  },
  fonts: (state, getters) => {
    const bodyFont = getters.bodyFont
    const buttonFont = getters.buttonFont
    const headingFont = getters.headingFont
    return [bodyFont, buttonFont, headingFont]
  },
  fontDetailBody: (state, getters) => {
    const bodyFont = getters.bodyFont
    const fontFamily = get(bodyFont, 'fields.fontFamily')
    const fontWeight = get(bodyFont, 'fields.fontWeight')
    const uppercase = get(bodyFont, 'fields.uppercase')
    return {
      fontFamily,
      fontWeight,
      uppercase,
    }
  },
  fontDetailButton: (state, getters) => {
    const buttonFont = getters.buttonFont
    const fontFamily = get(buttonFont, 'fields.fontFamily')
    const fontWeight = get(buttonFont, 'fields.fontWeight')
    const uppercase = get(buttonFont, 'fields.uppercase')
    return {
      fontFamily,
      fontWeight,
      uppercase,
    }
  },
  fontDetailHeading: (state, getters) => {
    const headingFont = getters.headingFont
    const fontFamily = get(headingFont, 'fields.fontFamily')
    const fontWeight = get(headingFont, 'fields.fontWeight')
    const uppercase = get(headingFont, 'fields.uppercase')
    const modifyLeading = get(headingFont, 'fields.modifyLeading')
    return {
      fontFamily,
      fontWeight,
      uppercase,
      modifyLeading,
    }
  },
  globallyEnabled: (state) => {
    const status = get(state, 'global.globallyEnabled')
    return status === 'Enabled'
  },
  hasMoreThanOneLocation: (state, getters) => {
    const locations = getters.locations
    return locations.length > 1
  },
  headingFont: (state) => {
    return get(state, 'brand.theme.fields.headingFont')
  },
  heroImage: (state) => {
    return get(state, 'brand.theme.fields.heroImage.fields.file.url')
  },
  lightLoadingColor: (state) => {
    return get(state, 'brand.theme.fields.lightLoadingColor')
  },
  locations: (state) => {
    const locations = get(state, 'brand.locations')
    return locations.map((location) => {
      const lat = get(location, 'fields.coordinates.lat')
      const lon = get(location, 'fields.coordinates.lon')
      if (lat && lon) {
        location.fields.latitude = lat
        location.fields.longitude = lon
      }
      return location
    })
  },
  locationsEnabled: (state, getters) => {
    const locations = getters.locations
    return locations.filter((location) => {
      const status = get(location, 'fields.enabled')
      return status === 'Enabled'
    })
  },
  locationLabel: (state) => {
    return get(state, 'global.locationLabel')
  },
  locationTitle: (state) => {
    return get(state, 'global.locationTitle')
  },
  logo: (state) => {
    return get(state, 'brand.theme.fields.logo.fields.file.url')
  },
  widerLogo: (state) => {
    return get(state, 'brand.theme.fields.widerLogo')
  },
  paymentTitle: (state) => {
    return get(state, 'global.paymentTitle')
  },
  paymentBodyText: (state) => {
    return get(state, 'global.paymentBodyText')
  },
  receiptBodyText: (state) => {
    return get(state, 'global.receiptBodyText')
  },
  receiptButtonText: (state) => {
    return get(state, 'global.receiptButtonText')
  },
  receiptLabel: (state) => {
    return get(state, 'global.receiptLabel')
  },
  receiptOptOutText: (state) => {
    return get(state, 'global.receiptOptOutText')
  },
  receiptTitle: (state) => {
    return get(state, 'global.receiptTitle')
  },
  singleRestaurantSlug: (state, getters) => {
    const locations = getters.locations
    return get(locations, '[0].fields.slug')
  },
  tableBodyText: (state) => {
    return get(state, 'global.tableBodyText')
  },
  tableButtonText: (state) => {
    return get(state, 'global.tableButtonText')
  },
  tableLabel: (state) => {
    return get(state, 'global.tableLabel')
  },
  termsText: (state) => {
    return get(state, 'brand.termsAndConditions.fields.text')
  },
  termsTitle: (state) => {
    return get(state, 'brand.termsAndConditions.fields.title')
  },
  tipBodyText: (state) => {
    return get(state, 'global.tipBodyText')
  },
  tipButtonText: (state) => {
    return get(state, 'global.tipButtonText')
  },
  tipDeclineText: (state) => {
    return get(state, 'global.tipDeclineText')
  },
  tipTitle: (state) => {
    return get(state, 'global.tipTitle')
  },
}

export const mutations = {
  setGlobal(state, data) {
    state.global = { ...data }
  },
  setBrand(state, data) {
    state.brand = { ...data }
  },
  setContentfulLoadingStatus(state, status) {
    state.contentfulLoadingStatus = status
  },
}

export const actions = {
  async getAndSetGlobalContentAndBrand({ commit }, { host }) {
    commit('setContentfulLoadingStatus', 'loading')
    try {
      const globalConfigContent = await getContentOne({
        contentType: 'globalConfig',
      })
      commit('setGlobal', globalConfigContent)

      const brandContent = await getContentOne({
        contentType: 'brand',
        fieldName: 'hostname',
        fieldValue: host,
        include: 2,
      })
      commit('setBrand', brandContent)
      commit('setContentfulLoadingStatus', 'loaded')
    } catch (err) {
      console.log(err)
      commit('setContentfulLoadingStatus', 'error')
    }
  },
}
